import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
  signal,
} from "@angular/core";
import * as Highcharts from "highcharts/highstock";
import {
  HC_LEGEND_STYLE,
  HC_CROSSHAIR_STYLE,
  HC_RANGE_SELECTOR,
} from "../../charts/highcharts-shared";
import { UtilService } from "../../shared/services/util.service";
import moment from "moment";
import {
  HighchartsChartComponent,
  providePartialHighcharts,
} from "highcharts-angular";

import { ChartPlaceholderComponent } from "../chart-placeholder/chart-placeholder.component";
import { InfoComponent } from "../info/info.component";

@Component({
  selector: "app-line-chart",
  templateUrl: "./line-chart.component.html",
  styleUrls: ["./line-chart.component.scss"],
  encapsulation: ViewEncapsulation.None,
  imports: [HighchartsChartComponent, ChartPlaceholderComponent, InfoComponent],
  standalone: true,
  providers: [
    providePartialHighcharts({
      modules: () => [
        import("highcharts/esm/modules/stock"),
        import("highcharts/esm/modules/series-label"),
      ],
    }),
  ],
})
export class LineChartComponent implements OnInit, OnChanges {
  @Input() series: any[] = [];
  @Input() fullHeight = false;
  @Input() extraOptions = {};
  @Input() chartTitle = "";
  @Input() benchmarks: any[] = [];
  @Input() benchmark: any = null;
  @Input() loaded = true;
  @Input() chartConstructor = "stockChart";
  @Input() startDate = null;
  @Input() endDate = null;

  @Input() isPrint = false;
  @Input() info = false;

  @Input() chartStart = null;
  @Input() chartEnd = null;
  @Input() title: string;

  @Output() afterSetExtremes: EventEmitter<any> = new EventEmitter<any>();

  internalReady = signal(false);
  chartInstance: Highcharts.Chart;

  Highcharts: typeof Highcharts = Highcharts;
  chartOptions: Highcharts.Options = {};

  util: UtilService;
  protected cdr: ChangeDetectorRef;
  constructor(private injector: Injector) {
    this.util = injector.get(UtilService);
    this.cdr = injector.get(ChangeDetectorRef);
  }

  ngOnInit(): void {
    // Only mount the chart when series has data; otherwise the placeholder
    // stays visible and a fresh chart is mounted later via ngOnChanges. This
    // prevents Highcharts from being initialized with empty data and then
    // failing to fully apply later option updates.
    if (this.series && this.series.length) {
      this.initChart();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.series) {
      this.internalReady.set(false);
      this.series = changes.series.currentValue;
      if (this.series && this.series.length) {
        this.initChart();
      }
    }
    if (changes.startDate || changes.endDate) {
      this.updateDates();
    }
  }

  updateDates() {
    if (this.chartInstance) {
      const axis: any = this.chartInstance.xAxis[0];
      const start = this.startDate
        ? moment(this.startDate).unix() * 1000
        : axis.dataMin;
      const end = this.endDate
        ? moment(this.endDate).unix() * 1000
        : axis.dataMax;
      axis.setExtremes(start, end);
    }
  }

  initChart() {
    this.chartOptions = {
      chart: {
        height: this.isPrint ? 460 : 380,
        type: "line",
        backgroundColor: "transparent",
        spacing: [12, 4, 8, 4],
        style: { fontFamily: "Inter, system-ui, sans-serif" },
        zooming: {
          mouseWheel: { enabled: false },
        },
      },
      credits: { enabled: false },
      title: { text: this.chartTitle },
      subtitle: { text: "" },
      xAxis: {
        type: "datetime",
        title: { text: "", style: { textTransform: "capitalize" } },
        lineColor: "transparent",
        tickColor: "transparent",
        gridLineWidth: 1,
        gridLineColor: "var(--border)",
        gridLineDashStyle: "ShortDash",
        labels: { style: { color: "var(--text-dim)" } },
        min: this.chartStart || null,
        max: this.chartEnd || null,
        crosshair: {
          ...HC_CROSSHAIR_STYLE,
          // Stock charts default to a date-label box on the axis. Disabled
          // here so the date is shown only inside the unified tooltip.
          label: { enabled: false },
        },
      },

      navigator: { enabled: false },
      scrollbar: { enabled: false },
      rangeSelector: {
        ...HC_RANGE_SELECTOR,
        buttons: [
          { type: "month", count: 1, text: "1M", title: "View 1 month" },
          { type: "month", count: 3, text: "3M", title: "View 3 months" },
          { type: "month", count: 6, text: "6M", title: "View 6 months" },
          { type: "ytd", text: "YTD", title: "View year to date" },
          { type: "year", count: 1, text: "1Y", title: "View 1 year" },
          { type: "all", text: "ALL", title: "View all" },
        ],
        selected: 5,
      },

      yAxis: {
        gridLineColor: "var(--border)",
        gridLineDashStyle: "ShortDash",
        gridLineWidth: 1,
        opposite: false,
        minorTickInterval: 0.5,
        title: {
          text: "Price",
          style: {
            textTransform: "capitalize",
            color: "var(--text-dim)",
            fontFamily: "Inter, system-ui, sans-serif",
            fontSize: "11px",
            fontWeight: "500",
          },
        },
        labels: { style: { color: "var(--text-dim)" } },
        minorGridLineWidth: 0,
        plotLines: [
          { value: 0, width: 1, color: "var(--text-faint)", zIndex: 10 },
        ],
      },
      legend: {
        // A single series makes the legend pointless — there is nothing to
        // toggle. Only show it once a benchmark (or any extra series) is added.
        enabled: this.series.length > 1,
        align: "left",
        verticalAlign: "bottom",
        layout: "horizontal",
        itemDistance: 18,
        margin: 12,
        padding: 0,
        symbolRadius: 2,
        symbolHeight: 3,
        symbolWidth: 14,
        squareSymbol: false,
        ...HC_LEGEND_STYLE,
      },
      tooltip: {
        // Stock charts force `outside: true` and `split: true` on top of the
        // global defaults. Override both so this chart uses the same single
        // box styling as every non-stock chart in the app.
        outside: false,
        split: false,
        valueDecimals: 2,
      },
      series: this.series,
      plotOptions: {
        trendline: { visible: false },
        series: {
          lineWidth: 2,
          dataGrouping: { enabled: false },
          label: { enabled: true },
          marker: {
            enabled: false,
            states: { hover: { enabled: true, radius: 4 } },
          },
          states: { hover: { lineWidthPlus: 0 } },
          animation: !this.isPrint,
        },
        column: {
          borderWidth: 0,
          dataLabels: {
            enabled: true,
            inside: false,
            verticalAlign: "top",
            align: "center",
            crop: false,
            overflow: "allow",
          },
        },
      },
    };
    if (this.fullHeight) {
      this.chartOptions.chart.height = "96%";
    }
    if (this.extraOptions) {
      this.chartOptions = {
        ...this.chartOptions,
        ...this.extraOptions,
      };
    }
    this.internalReady.set(true);
    this.cdr.markForCheck();
  }

  setChartInstance(chart: Highcharts.Chart) {
    this.chartInstance = chart;
  }
}
